import { memo } from "react";
import { cn } from "@/lib/utils";
import { NavRow } from "@/components/ui/nav-row";
import { AnimatedImage } from "@/components/ui/animated-image";
import { CoworkerIcon } from "@/components/ui/coworker-icon";
import { getPresenceState, presenceDotClass } from "@/lib/presence";
import type { DmActions } from "./use-dm-actions";

interface MemberLike {
  id: string;
  display_name: string;
  avatar_url?: string | null;
  is_online?: boolean | null;
  last_active_at?: number | null;
  is_coworker?: boolean | null;
}

interface MemberRowProps {
  user: MemberLike;
  /** When this user already has a DM channel, render selection +
   *  unread state against that channel's id. Undefined for users
   *  whose first message would create a new DM channel on click. */
  dmChannelId: string | undefined;
  isSelected: boolean;
  isUnread: boolean;
  actions: DmActions;
}

/**
 * Workspace-member row in the DM list. Renders an avatar + presence
 * dot + name. Click invokes `actions.openForUser(user.id)`, which
 * either opens the existing DM (if `dmChannelId` is set) or creates
 * one. No hover affordance — the row itself is the affordance.
 *
 * Wrapped in `memo()` and consumes `actions: DmActions` (stable ref)
 * so identity-stable props let `MemberRow.memo` bail out cleanly on
 * Sidebar re-renders.
 */
export const MemberRow = memo(function MemberRow({
  user,
  dmChannelId,
  isSelected,
  isUnread,
  actions,
}: MemberRowProps) {
  return (
    <div className="group/dm relative">
      <NavRow
        isSelected={dmChannelId ? isSelected : false}
        onClick={() => actions.openForUser(user.id)}
      >
        <div className="relative">
          <div className="h-5 w-5 rounded-full bg-foreground/20 flex items-center justify-center text-[9px] font-semibold overflow-hidden">
            {user.avatar_url ? (
              <AnimatedImage
                src={user.avatar_url}
                alt={user.display_name}
                className="h-full w-full object-cover"
              />
            ) : (
              user.display_name.slice(0, 2).toUpperCase()
            )}
          </div>
          <div
            className={cn(
              "absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full",
              presenceDotClass(
                getPresenceState({
                  is_online: Boolean(user.is_online),
                  last_active_at: user.last_active_at,
                }),
              ),
            )}
          />
        </div>
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            dmChannelId && isUnread && "font-bold text-foreground",
          )}
        >
          {user.display_name}
        </span>
        {user.is_coworker && (
          <CoworkerIcon size={12} className="shrink-0 text-muted-foreground" />
        )}
        {dmChannelId && isUnread && (
          <span className="ml-auto h-2 w-2 rounded-full bg-foreground shrink-0" />
        )}
      </NavRow>
    </div>
  );
});
